import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap"

import { ContactTopic, IContactEmail } from "@api/schema"
import FormikInput from "@components/common/form/FormikInput"
import FormikSelect from "@components/common/form/FormikSelect"
import GeneralButtonAndErrorArea from "@components/common/form/GeneralButtonAndErrorArea"
import { sendContactEmailAction } from "@redux/actions/processes"
import { IFormikActions } from "@redux/helper/actions"
import { hashIdContactPageBugReport } from "@services/processHelper"
import { useWindowLocation } from "@services/windowHooks"



interface IProps {
  formAsCard?: boolean
  initialTopic?: ContactTopic
}

/**
 * This component provides a contact form and a message, after the form was submitted
 */
const ContactForm = (props: IProps): JSX.Element => {
  const { formAsCard, initialTopic = ContactTopic.Feedback } = props
  const { t } = useTranslation("common")
  const dispatch = useDispatch()
  // need to remember the sent message in order to display the content after a successful submission
  const [sentMessage, setSentMessage] = useState<IContactEmail>(null)
  const [topic, setTopic] = useState<ContactTopic>(initialTopic)

  const initialContactEmail: IContactEmail = {
    senderEmail: '',
    senderName: '',
    message: '',
    topic
  }

  // needed to address the form to change the chosen topic if the hashId changes
  // e.g. if the contactform is linked in the page footer twice, to allow "normal contact" as well as "bug report contact"
  const formRef = useRef(null)

  /**
   * saving/changing the topic within the form after mounting or after external "change topic"-impulse
   */
  useEffect(() => {
    formRef?.current?.setFieldValue("topic", topic)
  }, [topic, formRef])

  const location = useWindowLocation()
  // if the hashId is set to "bugReport", changes the topic to BugReport, otherwise to Feedback
  useEffect(() => {
    if (location) {
      if (location.hash === hashIdContactPageBugReport) {
        setTopic(ContactTopic.BugReport)
      }
      else {
        setTopic(ContactTopic.Feedback)
      }
    }
    // reset message if the user has already sent a message and re-enters the form with a different location.hash
    if (sentMessage) {
      setSentMessage(null)
    }
  }, [location?.hash])

  /**
   * validating
   */
  const validate = (values: IContactEmail): FormikErrors<IContactEmail> => {
    const errors: { [key: string]: string } = {}

    if (values.senderName) {
      if (values.senderName.trim().length > 200) {
        errors.senderName = "validate.general.tooLong"
      }
    }
    if (values.message.trim().length === 0) {
      errors.message = "validate.general.notBlank"
    } else if (values.message.trim().length < 5) {
      errors.message = "validate.general.tooShort"
    } else if (values.message.trim().length > 1000) {
      errors.message = "validate.general.tooLong"
    }
    return errors
  }

  /**
   * submitting after data cleanup (e.g. trimming)
   */
  const onSubmit = (values: IContactEmail, actions: IFormikActions): void => {
    // remove trailing spaces
    values.message = values.message.trim()
    values.senderEmail = values.senderEmail.trim()
    values.senderName = values.senderName.trim()
    // save user's message for displaying it when submitted
    actions.onSuccess = () => {
      setSentMessage(values)
    }
    // send user's message to the backend
    dispatch(sendContactEmailAction(values, actions))
  }

  /**
   * formFields separated from form, to allow embedding in Card, or without Card
   */
  const formFields = (values: IContactEmail) => <>
    <Field component={FormikSelect}
      help="form.process.contact.topic.help"
      label="form.process.contact.topic.label"
      name="topic"
      options={{
        [t("form.process.contact.topic.options." + ContactTopic.BugReport)]: ContactTopic.BugReport,
        [t("form.process.contact.topic.options." + ContactTopic.Feedback)]: ContactTopic.Feedback,
      }}
      value={values.topic}
      aria-label={t("form.process.contact.topic.placeholder")}
    />
    <Field component={FormikInput}
      help="form.process.contact.senderName.help"
      label="form.process.contact.senderName.label"
      name="senderName"
      placeholder="form.process.contact.senderName.placeholder"
      value={values.senderName}
      aria-label={t("form.process.contact.senderName.placeholder")}
    />
    <Field component={FormikInput}
      help="form.process.contact.senderEmail.help"
      label="form.process.contact.senderEmail.label"
      name="senderEmail"
      type="email"
      placeholder="form.process.contact.senderEmail.placeholder"
      value={values.senderEmail}
      aria-label={t("form.process.contact.senderEmail.placeholder")}
    />
    <Field component={FormikInput}
      help="form.process.contact.message.help"
      label="form.process.contact.message.label"
      name="message"
      placeholder="form.process.contact.message.placeholder"
      value={values.message}
      type="textarea"
      maxLength={1000}
      aria-label={t("form.process.contact.message.placeholder")}
    />
  </>

  /**
   * the form
   */
  const contactForm = <Formik<IContactEmail>
    initialValues={initialContactEmail}
    onSubmit={onSubmit}
    validate={validate}
    validateOnBlur={false}
    validateOnChange={false}
    innerRef={formRef}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      isValidating,
      values
    }) => <Form onSubmit={handleSubmit}>
        {formAsCard
          ? <Card>
            <CardBody>
              {formFields(values)}
            </CardBody>
          </Card>
          : formFields(values)
        }
        {
          /* @todo "required" asterisk
           * The "required" asterisk will only be attached to the label, if the label is not empty and the input has the property requiered.
           * This is defined in our general FormikInput-Component.
           *
           * The translations for the labels are empty and therefor the "required" asterisk will not be attached to the label
           *
           * <p className="text-danger">{t("form.requiredFieldsHint")}</p>
          */
        }
        <GeneralButtonAndErrorArea
          excludedFieldsGeneralErrorForm={["senderEmail", "senderName", "message", "topic"]}
          prefixGeneralErrorForm="process"
          errors={errors}
          isSubmitting={isSubmitting}
          isValidating={isValidating}
          titleSubmitButton="form.submit"
          iconNameSubmitButton="message"
        />
      </Form>
    }
  </Formik>



  return <div className="contactform">
    {!sentMessage
      ? <>
        {contactForm}
      </>
      : <Card>
        <CardHeader>
          <Row>
            <Col>
              <h4>{t("form.process.contact.response.thankYou")}</h4>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {(sentMessage.senderName || sentMessage.senderEmail) &&
            <div className="form-group">
              <div className="rte-content">{t("form.process.contact.response.from", { sender: sentMessage.senderName + (sentMessage.senderEmail ? ", " + sentMessage.senderEmail : "") })}</div>
            </div>
          }
          <div className="form-group">
            <div className="rte-content">{t("form.process.contact.topic.options." + sentMessage.topic)}</div>
          </div>
          <div className="form-group">
            <div className="rte-content">{sentMessage.message}</div>
          </div>
        </CardBody>
      </Card>
    }</div>
}

export default ContactForm